import React, { useEffect, useRef } from 'react'

import { VirtualCandidateSpeaker } from 'src/services/backend/VirtualCandidateService'

export interface SpeakerIndicatorProps {
    /** Who is audible right now, as the service reports it. */
    speaking: VirtualCandidateSpeaker | null
    /** Live audio, so the active speaker's ring follows how loud they are. */
    analyser?: AnalyserNode
}

/** Each speaker's name and color, shared by the speaker circles and the transcript. */
export const SPEAKER_STYLE: Readonly<
    Record<VirtualCandidateSpeaker, { readonly name: string; readonly color: string }>
> = {
    CANDIDATE: { name: 'Candidate', color: '#7b4bdb' },
    IRENE: { name: 'Irene', color: '#0f9d9a' },
}

/** Left to right: the candidate, then Irene, the iScreen interviewer. */
const SPEAKERS: readonly {
    id: VirtualCandidateSpeaker
    label: string
    /** Who is named while speaking. */
    voice: string
    color: string
}[] = (['CANDIDATE', 'IRENE'] as const).map((id) => ({
    id,
    label: SPEAKER_STYLE[id].name,
    voice: SPEAKER_STYLE[id].name,
    color: SPEAKER_STYLE[id].color,
}))

const STYLE = {
    circleSize: 56,
    idleColor: '#d5dbdb',
    idleText: '#8c99a6',
    /** Glow radius in px at silence and at full volume. */
    glowMin: 10,
    glowMax: 34,
    /** How far the circle grows at full volume. */
    growth: 0.18,
    dimmedOpacity: 0.45,
    gap: 72,
} as const

const BYTE_MIDPOINT = 128
/** Speech rarely exceeds a third of full scale, so the pulse reaches full size at normal volume. */
const LEVEL_GAIN = 3

/** Loudness from 0 (silence) to 1. */
export const levelOf = (waveform: Uint8Array): number => {
    if (waveform.length === 0) return 0
    let sum = 0
    for (let i = 0; i < waveform.length; i++) {
        const centered = (waveform[i] - BYTE_MIDPOINT) / BYTE_MIDPOINT
        sum += centered * centered
    }
    return Math.min(1, Math.sqrt(sum / waveform.length) * LEVEL_GAIN)
}

const glow = (color: string, level: number) =>
    `0 0 ${STYLE.glowMin + level * (STYLE.glowMax - STYLE.glowMin)}px ${color}`

/** Pulses the circle with the audio level every animation frame, without re-rendering React. */
const usePulse = (analyser: AnalyserNode | undefined, active: boolean, color: string) => {
    const circleRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const circle = circleRef.current
        if (!circle) return undefined
        if (!active || !analyser) {
            circle.style.transform = 'scale(1)'
            circle.style.boxShadow = active ? glow(color, 0) : 'none'
            return undefined
        }
        const waveform = new Uint8Array(analyser.fftSize)
        let frame = 0
        const draw = () => {
            analyser.getByteTimeDomainData(waveform)
            const level = levelOf(waveform)
            circle.style.transform = `scale(${1 + level * STYLE.growth})`
            circle.style.boxShadow = glow(color, level)
            frame = requestAnimationFrame(draw)
        }
        frame = requestAnimationFrame(draw)
        return () => cancelAnimationFrame(frame)
    }, [analyser, active, color])
    return circleRef
}

const SpeakerCircle = ({
    label,
    voice,
    color,
    active,
    dimmed,
    analyser,
}: {
    label: string
    voice: string
    color: string
    active: boolean
    /** The other side is speaking. */
    dimmed: boolean
    analyser?: AnalyserNode
}) => {
    const circleRef = usePulse(analyser, active, color)
    return (
        <div
            data-test-id={`speaker-${label.toLowerCase()}`}
            data-active={active}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                opacity: dimmed ? STYLE.dimmedOpacity : 1,
                transition: 'opacity 200ms ease',
            }}
        >
            <div
                ref={circleRef}
                aria-hidden
                style={{
                    width: STYLE.circleSize,
                    height: STYLE.circleSize,
                    borderRadius: '50%',
                    background: active ? color : STYLE.idleColor,
                    border: `3px solid ${active ? color : STYLE.idleColor}`,
                    transition:
                        'background 200ms ease, border-color 200ms ease, transform 80ms linear',
                }}
            />
            <span
                style={{
                    fontWeight: active ? 'bold' : 'normal',
                    color: active ? color : STYLE.idleText,
                }}
            >
                {active ? `${voice} speaking` : label}
            </span>
        </div>
    )
}

/** Two circles, the candidate and Irene; the one talking glows in its color and pulses with its voice. */
export const SpeakerIndicator = ({ speaking, analyser }: SpeakerIndicatorProps) => {
    const current = SPEAKERS.find((speaker) => speaker.id === speaking)
    return (
        <div
            role='status'
            aria-label={current ? `${current.voice} is speaking` : 'Nobody is speaking'}
            data-test-id='virtual-candidate-speakers'
            style={{ display: 'flex', justifyContent: 'center', gap: STYLE.gap, padding: '20px 0' }}
        >
            {SPEAKERS.map((speaker) => (
                <SpeakerCircle
                    key={speaker.id}
                    label={speaker.label}
                    voice={speaker.voice}
                    color={speaker.color}
                    active={speaker.id === speaking}
                    dimmed={Boolean(current) && speaker.id !== speaking}
                    analyser={analyser}
                />
            ))}
        </div>
    )
}
